// FormOptionsDrawer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import "./FormOptionsDrawer.css";

export default function FormOptionsDrawer({
  control,
  viewfieldsInstance,
  languages = ["en"],
  lang = "en",
  onApplyInline,   // (normalizedInlineArray) => void (form-level change)
  onChangePhrase,  // currently unused but kept for API compatibility
  onClose,
  // (assetKey, contentArray) => void (asset-level change)
  // contentArray: [{ name, value }, ...] – raw S3 JSON, no wrappers/metadata
  onExportToAsset,
}) {
  const drawerRef = useRef(null);

  // Seed from inline (preserve native types exactly)
  const inlineSeed = useMemo(() => {
    if (!Array.isArray(control?.options)) return [];
    return control.options.map((o) => ({
      _id: null,
      name: o?.name ?? "",
      value: o?.value, // keep original type
      valueMode: typeof o?.value === "boolean" ? "bool" : "text",
    }));
  }, [control?.options]);

  // Seed from asset (now editable; no longer "read-only")
const assetSeed = useMemo(() => {
  try {
    const out = viewfieldsInstance?.resolveOptionsForFieldExternal?.(control);

    console.log("[FormOptionsDrawer] assetSeed resolveOptionsForFieldExternal", {
      controlKey: control?.key,
      controlType: control?.type,
      hasInlineOptions: Array.isArray(control?.options),
      resolved: out,
      flatOptions: Array.isArray(out?.flatOptions) ? out.flatOptions : null,
      assetRegistryVersion: viewfieldsInstance?.assetRegistryVersion, // if you have something like this
    });

    const flat = Array.isArray(out?.flatOptions) ? out.flatOptions : [];
    return flat.map((o) => ({
      _id: null,
      name: typeof o?.name === "string" && o.name.length > 0 ? o.name : "(Missing option name)",
      value: o?.value,
      valueMode: typeof o?.value === "boolean" ? "bool" : "text",
    }));
  } catch (err) {
    console.log("[FormOptionsDrawer] assetSeed ERROR", { err, control });
    return [];
  }
}, [control, viewfieldsInstance]);

  // Mode: Inline if inline options exist, else Asset
  const [mode, setMode] = useState(
    Array.isArray(control?.options) ? "INLINE" : "ASSET"
  );
  useEffect(() => {
    setMode(Array.isArray(control?.options) ? "INLINE" : "ASSET");
  }, [control?.options]);

  // Seed working rows from current mode
  const nextUid = useRef(1);
  const seededRows = useMemo(() => {
    const base = mode === "INLINE" ? inlineSeed : assetSeed;
    return base.map((r) => ({
      _id: r._id || `opt_${nextUid.current++}`,
      name: r.name,
      value: r.value,
      valueMode:
        r.valueMode || (typeof r.value === "boolean" ? "bool" : "text"),
    }));
  }, [mode, inlineSeed, assetSeed]);

  const [drawerLang, setDrawerLang] = useState(lang);
  useEffect(() => {
    setDrawerLang(lang);
  }, [lang]);

  const [rows, setRows] = useState(seededRows);
  // re-seed rows when control/mode changes (e.g. new control while panel open)
  useEffect(() => {
    setRows(seededRows);
  }, [seededRows]);

  // Duplicate name check (must be unique & non-empty)
  const dupNameSet = useMemo(() => {
    const counts = rows.reduce((acc, r) => {
      const k = (r.name || "").trim();
      if (!k) return acc;
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  }, [rows]);
  const hasBlockingError = dupNameSet.size > 0;

  // Change tracking vs seededRows
  const hasChanges = useMemo(() => {
    if (rows.length !== seededRows.length) return true;
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      const b = seededRows[i];
      if (!b) return true;
      if (
        r.name !== b.name ||
        r.value !== b.value ||
        r.valueMode !== b.valueMode
      ) {
        return true;
      }
    }
    return false;
  }, [rows, seededRows]);

  // DnD (allowed in both INLINE and ASSET modes)
  const dragIndex = useRef(-1);
  const handleDragStart = (i) => (e) => {
    dragIndex.current = i;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = -1;
    if (from === -1 || from === i) return;
    setRows((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(i, 0, moved);
      return arr;
    });
  };

  // CRUD (allowed in both modes)
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        _id: `opt_${nextUid.current++}`,
        name: "",
        value: "",
        valueMode: "text",
      },
    ]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // Copy Name → Value (disabled for boolean-mode rows)
  const copyNameToValue = (idx) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        if (r.valueMode === "bool") return r;
        return { ...r, value: String(r.name ?? "") };
      })
    );
  };

  // Switch from controlled boolean mode to manual text value
  const convertBoolToTextValue = (idx) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const current = r.value;
        return {
          ...r,
          valueMode: "text",
          value: current === undefined || current === null ? "" : String(current),
        };
      })
    );
  };

  // Sort by raw name
  const handleAlphabetizeByName = () => {
    setRows((prev) => {
      const items = prev.map((r, i) => ({ ...r, _i: i }));
      items.sort((a, b) => {
        const na = String(a.name || "");
        const nb = String(b.name || "");
        const cmp = na.localeCompare(nb, undefined, { sensitivity: "base" });
        return cmp !== 0 ? cmp : a._i - b._i;
      });
      return items.map(({ _i, ...rest }) => rest);
    });
  };

  // Asset → Inline import (for people who still prefer editing as inline)
  const importAssetToInline = () => {
    const base = assetSeed.map((r) => ({
      _id: `opt_${nextUid.current++}`,
      name: r.name,
      value: r.value,
      valueMode:
        r.valueMode || (typeof r.value === "boolean" ? "bool" : "text"),
    }));
    setRows(base);
    setMode("INLINE");
  };

  // Inline → Asset export (create or overwrite) – used in INLINE mode
  const exportInlineToAsset = () => {
    const assetKey = control?.key || control?.field;
    if (!assetKey) {
      console.log(
        "[FormOptionsDrawer] exportInlineToAsset: missing assetKey from control",
        control
      );
      return;
    }

    const content = rows.map((r) => ({
      name: String(r.name ?? ""),
      value: r.value,
    }));

    console.log("[FormOptionsDrawer] exportInlineToAsset", {
      assetKey,
      optionsCount: content.length,
      sample: content.slice(0, 5),
    });

    if (typeof onExportToAsset === "function") {
      // IMPORTANT: raw content array only, no wrappers/metadata
      onExportToAsset(assetKey, content);
    }

    // After exporting, close the drawer; the form/editor will handle pending state.
    if (typeof onClose === "function") {
      onClose();
    }
  };

  // Apply (mode-aware dispatcher) – INLINE → form
  const applyInline = () => {
    if (hasBlockingError || !hasChanges) return;
    const next = rows.map((r) => ({
      name: String(r.name ?? ""),
      value: r.value,
    }));
    if (typeof onApplyInline === "function") {
      onApplyInline(next);
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  // ASSET apply – overwrite existing asset in place (no inline options)
  const applyAsset = () => {
    if (hasBlockingError || !hasChanges) {
      if (typeof onClose === "function") {
        onClose();
      }
      return;
    }

    const assetKey = control?.key || control?.field;
    if (!assetKey || typeof onExportToAsset !== "function") {
      if (typeof onClose === "function") {
        onClose();
      }
      return;
    }

    const content = rows.map((r) => ({
      name: String(r.name ?? ""),
      value: r.value,
    }));

    console.log("[FormOptionsDrawer] applyAsset overwrite", {
      assetKey,
      optionsCount: content.length,
      sample: content.slice(0, 5),
    });

    // IMPORTANT: raw content array only, no wrappers/metadata
    onExportToAsset(assetKey, content);

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleApply = () => {
    if (mode === "INLINE") {
      applyInline();
    } else if (mode === "ASSET") {
      applyAsset();
    }
  };

  const applyDisabled = hasBlockingError || !hasChanges;
  const applyTitle =
    mode === "INLINE"
      ? hasBlockingError
        ? "Fix duplicate names first"
        : hasChanges
        ? "Apply inline options"
        : "No changes to apply"
      : hasBlockingError
      ? "Fix duplicate names first"
      : hasChanges
      ? "Overwrite asset options"
      : "No changes to apply";

  // ESC to close (component only exists while panel is open)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isRadioControl =
    control?.type === "radio" || control?.controlType === "radio";

  return (
    <div className="fe-opts" role="group" ref={drawerRef}>
      <div className="fe-opts__header">
        <div className="fe-opts__title">Options</div>
        <div
          className="fe-opts__langgroup"
          role="tablist"
          aria-label="Language"
        >
          {languages.map((l) => (
            <button
              key={l}
              role="tab"
              aria-selected={drawerLang === l}
              className={`fe-opts__lang ${
                drawerLang === l ? "is-active" : ""
              }`}
              onClick={() => setDrawerLang(l)}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Mode toggle + Import/Export */}
      <div className="fe-opts__toolbar">
        <div
          className="fe-opts__mode"
          role="tablist"
          aria-label="Options source"
        >
          <button
            role="tab"
            aria-selected={mode === "INLINE"}
            className={`fe-opts__seg ${mode === "INLINE" ? "is-active" : ""}`}
            onClick={() => setMode("INLINE")}
          >
            Inline
          </button>
          <button
            role="tab"
            aria-selected={mode === "ASSET"}
            className={`fe-opts__seg ${mode === "ASSET" ? "is-active" : ""}`}
            onClick={() => setMode("ASSET")}
          >
            Asset
          </button>
        </div>

        <div className="fe-opts__toolbarbtns">
          {mode === "INLINE" && (
            <>
              <button className="fe-opts__btn" onClick={addRow}>
                Add option
              </button>
              <button
                className="fe-opts__btn"
                onClick={handleAlphabetizeByName}
              >
                Alphabetize by name
              </button>
              <button
                className="fe-opts__btn"
                onClick={exportInlineToAsset}
                title="Export current inline options to asset"
              >
                Export to Asset
              </button>
            </>
          )}
          {mode === "ASSET" && (
            <button
              className="fe-opts__btn"
              onClick={importAssetToInline}
              title="Import current asset options into inline list for editing"
            >
              Import to Inline
            </button>
          )}
        </div>
      </div>

      {/* Rows */}
      <div className="fe-opts__list" role="list">
        {rows.map((row, i) => {
          const isDup = row.name && dupNameSet.has(row.name.trim());
          const isBooleanMode = row.valueMode === "bool";

          const fieldsClassName = [
            "fe-opts__fields",
            isBooleanMode ? "fe-opts__fields--bool" : "",
            isRadioControl ? "fe-opts__fields--radio" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={row._id}
              className="fe-opts__row"
              role="listitem"
              draggable
              onDragStart={handleDragStart(i)}
              onDragOver={handleDragOver(i)}
              onDrop={handleDrop(i)}
            >
              <div
                className="fe-opts__drag"
                title="Drag to reorder"
                aria-label="Drag handle"
              >
                ⋮⋮
              </div>

              <div className={fieldsClassName}>
                <div className="fe-opts__fieldgroup">
                  <label className="fe-opts__name_label">Name</label>
                  <input
                    className={`fe-opts__input ${isDup ? "has-error" : ""}`}
                    value={row.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    placeholder="Name"
                  />
                  {isDup && (
                    <div className="fe-opts__err">Name must be unique</div>
                  )}
                </div>

                {/* Copy Name → Value (disabled for boolean-mode rows) */}
                <button
                  className="fe-opts__copy"
                  title={
                    isBooleanMode
                      ? "Copy disabled for boolean values"
                      : "Set value equal to name"
                  }
                  onClick={() => !isBooleanMode && copyNameToValue(i)}
                  aria-label="Copy name to value"
                  disabled={isBooleanMode}
                >
                  ⇄
                </button>

                <div className="fe-opts__fieldgroup fe-opts__fieldgroup--grow">
                  <label className="fe-opts__value_label">Value</label>

                  {isBooleanMode ? (
                    <div className="fe-opts__bool">
                      <Switch
                        checked={row.value === true}
                        onChange={(e) =>
                          updateRow(i, {
                            value: e.target.checked,
                          })
                        }
                        inputProps={{ "aria-label": "Toggle value" }}
                      />
                      <span className="fe-opts__boollabel" aria-live="polite">
                        {row.value ? "true" : "false"}
                      </span>
                      <button
                        type="button"
                        className="fe-opts__bool-edit"
                        onClick={() => convertBoolToTextValue(i)}
                        title="Edit value manually"
                      >
                        ✎
                      </button>
                    </div>
                  ) : (
                    <input
                      className="fe-opts__input"
                      value={String(row.value ?? "")}
                      onChange={(e) =>
                        updateRow(i, { value: e.target.value })
                      }
                      placeholder="Value"
                    />
                  )}
                </div>
              </div>

              <div className="fe-opts__spacer" />
              <button
                className="fe-opts__remove"
                onClick={() => removeRow(i)}
                aria-label="Remove option"
                title="Remove option"
              >
                🗑
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer: status + Cancel + Apply */}
      <div className="fe-opts__footer">
        <div className="fe-opts__status">
          {hasBlockingError ? (
            <span className="fe-opts__err">Fix duplicate names</span>
          ) : (
            <span className="fe-opts__hint">
              {hasChanges ? "Unsaved changes" : "No changes"}
            </span>
          )}
        </div>
        <div className="fe-opts__footerbtns">
          <button
            className="fe-opts__btn fe-opts__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="fe-opts__btn fe-opts__btn--primary"
            onClick={handleApply}
            disabled={applyDisabled}
            title={applyTitle}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
